import { Link } from "wouter";
import { useUser } from "@/hooks/use-user";
import { useHasRole } from "@/hooks/use-role";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { useTranslation } from "react-i18next";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import {
  Menu,
  User,
  LogOut,
  CircleDollarSign,
  Calendar,
  Activity,
  Star,
  Home,
  GiftIcon,
  MessageSquare,
  Shield,
  Building2,
} from "lucide-react";

interface HeaderProps {
  siteName?: string;
}

export default function Header({ siteName = "NettoiGo" }: HeaderProps) {
  const { t } = useTranslation("header");
  const { user, logout, isLoading } = useUser();
  // Always call the hook, but its implementation will check if user exists
  const { unreadCount } = useUnreadMessages();
  const isAdmin = useHasRole("admin");
  const isClient = useHasRole("client");
  const isCleaner = useHasRole("cleaner");
  const isCleaningCompany = useHasRole("cleaning_company");
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "nettoigo.ca";
  const isNettoigoDomain =
    hostname === "nettoigo.ca" || hostname === "localhost";
  // if (isNettoigoDomain) {
  siteName = "NettoiGo"; // TODO: Remove this once we have a proper domain name
  // } else {
  //   siteName = "WeKlean";
  // }
  return (
    <header className="border-b bg-background">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link href="/" className="flex items-center gap-2">
              <span className="font-bold text-lg hidden md:inline-block">
                {siteName}
              </span>
              <img
                src="/images/logo.png"
                alt={siteName}
                className="h-8 w-auto"
              />
            </Link>

            {!user && (
              <nav className="hidden md:flex items-center gap-6">
                <Link
                  href="/homeowners"
                  className="text-slate-600 hover:text-primary"
                >
                  {t("forHomeowners")}
                </Link>
                <Link
                  href="/gallery"
                  className="text-slate-600 hover:text-primary"
                >
                  {t("gallery")}
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-slate-600 hover:text-primary px-0"
                    >
                      {t("becomeCleaner")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem asChild>
                      <Link href="/cleaners/join">Devenir nettoyeur</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/emplois-nettoyage-quebec">
                        Emplois au Québec
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/emplois-menage-montreal">
                        Emplois à Montréal
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/nettoyeur-independant-quebec">
                        Travail indépendant
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </nav>
            )}
          </div>

          {user ? (
            <div className="flex items-center gap-4">
              <RoleSwitcher />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    {user.profilePic ? (
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.profilePic} alt={user.name} />
                        <AvatarFallback>{user.name[0]}</AvatarFallback>
                      </Avatar>
                    ) : null}
                    <span className="font-medium hidden md:inline">
                      {user.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link href="/admin">
                        <div className="flex items-center">
                          <Shield className="mr-2 h-4 w-4" />
                          <span>{t("admin")}</span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isCleaningCompany && (
                    <DropdownMenuItem asChild>
                      <Link href="/company/dashboard">
                        <div className="flex items-center">
                          <Building2 className="mr-2 h-4 w-4" />
                          <span>{t("companyDashboard", "Company Dashboard")}</span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link href="/profile">
                      <div className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        <span>{t("myAccount")}</span>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  {(isClient || isCleaner) && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link href="/bookings">
                          <div className="flex items-center">
                            <Calendar className="mr-2 h-4 w-4" />
                            <span>{t("myBookings")}</span>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {isClient && (
                    <DropdownMenuItem asChild>
                      <Link href="/properties">
                        <div className="flex items-center">
                          <Home className="mr-2 h-4 w-4" />
                          <span>{t("properties")}</span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isCleaner && (
                    <DropdownMenuItem asChild>
                      <Link href="/profile/payouts">
                        <div className="flex items-center">
                          <CircleDollarSign className="mr-2 h-4 w-4" />
                          <span>{t("earningsPayouts")}</span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Messages menu item - visible to all users except admin-only users */}
                  {(isClient || isCleaner) && (
                    <DropdownMenuItem asChild>
                      <Link href="/messages">
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center">
                            <MessageSquare className="mr-2 h-4 w-4" />
                            <span>{t("messages")}</span>
                          </div>
                          {unreadCount > 0 && (
                            <Badge
                              variant="destructive"
                              className="rounded-full px-2 py-0.5 text-xs"
                            >
                              {unreadCount}
                            </Badge>
                          )}
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link href="/referrals">
                      <div className="flex items-center">
                        <GiftIcon className="mr-2 h-4 w-4" />
                        <span>{t("referralProgram")}</span>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => logout()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t("logout")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Button variant="outline" asChild>
                <Link href="/cleaners/join">{t("becomeCleaner")}</Link>
              </Button>
              <Button asChild>
                <Link href="/login">{t("login")}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
